import React, { memo, MouseEvent } from "react";
import { Box, Avatar, IconButton, Fade, CircularProgress, Tooltip } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import chatbotProfileIcon from "../../../../../assets/img/chatbot-profile-icon.svg";
import { Message } from "../../../../../shared/types/chatWindow.types";

export type AudioPlaybackState = "idle" | "loading" | "playing" | "paused";

interface ChatMessageItemProps {
    msg: Message;
    isHovered: boolean;
    isActive: boolean;
    alwaysShowActions: boolean;
    baseStyles: Record<string, any>;
    markdownComponents: Record<string, any>;
    onMouseEnter: (id: string) => void;
    onMouseLeave: () => void;
    onMenuOpen: (e: MouseEvent<HTMLElement>, msg: Message) => void;
    activeAudioId?: string | null;
    playbackState?: AudioPlaybackState;
    onTogglePlayAudio?: (messageId: string, text: string) => void;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = memo(({
    msg,
    isHovered,
    isActive,
    alwaysShowActions,
    baseStyles,
    markdownComponents,
    onMouseEnter,
    onMouseLeave,
    onMenuOpen,
    activeAudioId,
    playbackState = "idle",
    onTogglePlayAudio,
}) => {
    const isBot = msg.isBot;
    const msgId = String(msg.id);
    const isCurrentAudio = activeAudioId === msgId;
    const isAudioActive = isCurrentAudio && playbackState !== "idle";
    const showActions = isBot && (alwaysShowActions || isHovered || isActive || isAudioActive);

    return (
        <Fade in timeout={400}>
            <Box
                onMouseEnter={() => onMouseEnter(msgId)}
                onMouseLeave={onMouseLeave}
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: isBot ? "flex-start" : "flex-end",
                    gap: 1,
                    animation: "fadeInUp 0.25s ease-out",
                    "@keyframes fadeInUp": {
                        from: {
                            opacity: 0,
                            transform: "translateY(8px)",
                        },
                        to: {
                            opacity: 1,
                            transform: "translateY(0)",
                        },
                    },
                }}
            >
                {isBot && (
                    <Avatar src={chatbotProfileIcon} alt="Chatbot" sx={{ width: 36, height: 36 }} />
                )}

                <Box
                    id={`msg-content-${msg.id}`}
                    sx={{
                        ...baseStyles,
                        bgcolor: isBot ? "#c9d7dd" : "#a1d2df",
                        color: isBot ? "#0f172a" : "white",
                    }}
                >
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.text || ""}
                    </ReactMarkdown>
                </Box>

                {isBot && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {showActions && (
                            <>
                                {/* Audio Player Button */}
                                {isCurrentAudio && playbackState === "loading" ? (
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CircularProgress size={16} sx={{ color: "#5FB7CF" }} />
                                    </Box>
                                ) : isCurrentAudio && playbackState === "playing" ? (
                                    <Tooltip title="Pause audio">
                                        <IconButton
                                            size="small"
                                            onClick={() => onTogglePlayAudio?.(msgId, msg.text)}
                                            sx={{
                                                bgcolor: "#e2e8f0",
                                                color: "#5FB7CF",
                                                "&:hover": { bgcolor: "#cbd5e1" },
                                            }}
                                        >
                                            <PauseCircleIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={
                                            isCurrentAudio && playbackState === "paused"
                                                ? "Resume audio"
                                                : "Read aloud"
                                        }
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => onTogglePlayAudio?.(msgId, msg.text)}
                                            sx={{
                                                bgcolor: "#e2e8f0",
                                                color: isCurrentAudio && playbackState === "paused" ? "#5FB7CF" : "#64748b",
                                                "&:hover": { bgcolor: "#cbd5e1", color: "#5FB7CF" },
                                            }}
                                        >
                                            {isCurrentAudio && playbackState === "paused" ? (
                                                <PlayCircleIcon fontSize="small" />
                                            ) : (
                                                <VolumeUpIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Context Menu Button */}
                                <IconButton
                                    size="small"
                                    onClick={(e) => onMenuOpen(e, msg)}
                                    sx={{
                                        bgcolor: "#e2e8f0",
                                        "&:hover": { bgcolor: "#cbd5e1" },
                                    }}
                                >
                                    <MoreHorizIcon fontSize="small" sx={{ color: "#64748b" }} />
                                </IconButton>
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Fade>
    );
});

export default ChatMessageItem;